import {
  getClientCatalogRealtimeToken,
  resolveClientCatalogRealtimeWebsocketUrl,
} from '../api/clientPortalApi';

const listeners = new Set();
let socket = null;
let reconnectTimer = null;
let scopeRefreshTimer = null;
let reconnectAttempts = 0;
let connectionGeneration = 0;
let running = false;
let isOpening = false;

const MAIN_BRANCH_CHANGED_ACTION = 'main-branch-changed';
const DEFAULT_TOKEN_TTL_SECONDS = 120;
const TOKEN_REFRESH_RATIO = 0.9;

const notifyListeners = (payload) => {
  listeners.forEach((listener) => {
    try {
      listener(payload);
    } catch (error) {
      console.error('Error in client catalog event listener:', error);
    }
  });
};

const clearReconnect = () => {
  if (!reconnectTimer) return;
  clearTimeout(reconnectTimer);
  reconnectTimer = null;
};

const clearScopeRefresh = () => {
  if (!scopeRefreshTimer) return;
  clearTimeout(scopeRefreshTimer);
  scopeRefreshTimer = null;
};

const closeSocket = () => {
  if (!socket) return;
  const previous = socket;
  socket = null;
  try {
    previous.close();
  } catch (error) {
    console.error('Error closing client catalog WebSocket:', error);
  }
};

const scheduleReconnect = () => {
  clearReconnect();
  clearScopeRefresh();
  if (!running) return;
  const delay = Math.min(15000, 1000 * 2 ** reconnectAttempts);
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(() => {
    openSocket().catch((error) => {
      console.error('Error reconnecting client catalog WebSocket:', error);
    });
  }, delay);
};

const scheduleScopeRefresh = (expiresInSeconds) => {
  clearScopeRefresh();
  if (!running) return;
  const parsedTtlSeconds = Number(expiresInSeconds);
  const ttlSeconds = Number.isFinite(parsedTtlSeconds) && parsedTtlSeconds >= 1
    ? parsedTtlSeconds
    : DEFAULT_TOKEN_TTL_SECONDS;
  const delay = Math.max(100, Math.floor(ttlSeconds * 1000 * TOKEN_REFRESH_RATIO));
  scopeRefreshTimer = setTimeout(() => {
    scopeRefreshTimer = null;
    reconnectWithFreshScope();
  }, delay);
};

const reconnectWithFreshScope = () => {
  if (!running) return;
  reconnectAttempts = 0;
  connectionGeneration += 1;
  isOpening = false;
  clearReconnect();
  clearScopeRefresh();
  closeSocket();
  openSocket().catch((error) => {
    console.error('Error refreshing client catalog WebSocket scope:', error);
  });
};

const openSocket = async () => {
  if (!running || isOpening) return;
  const generation = connectionGeneration;
  isOpening = true;
  clearReconnect();
  clearScopeRefresh();
  let config;
  try {
    config = await getClientCatalogRealtimeToken();
  } catch (error) {
    if (generation !== connectionGeneration) return;
    console.error(
      'Error fetching client catalog realtime token:',
      error?.response?.data?.message || error?.message || error
    );
    scheduleReconnect();
    return;
  } finally {
    if (generation === connectionGeneration) isOpening = false;
  }
  if (generation !== connectionGeneration || !running) return;
  if (!config?.token || !config?.branchId || !config?.companyId) {
    console.error('Client catalog realtime token was missing its scope.');
    scheduleReconnect();
    return;
  }

  const websocketUrl = resolveClientCatalogRealtimeWebsocketUrl(
    config.websocketUrl || `/ws2/client-catalog/${config.branchId}`,
    config.token
  );
  const nextSocket = new WebSocket(websocketUrl);
  socket = nextSocket;
  nextSocket.onopen = () => {
    if (socket !== nextSocket || generation !== connectionGeneration) return;
    reconnectAttempts = 0;
    notifyListeners({
      type: 'products.invalidated',
      action: 'client-catalog-connected',
      companyId: config.companyId,
      branchId: config.branchId,
    });
  };
  nextSocket.onmessage = (event) => {
    if (socket !== nextSocket || generation !== connectionGeneration) return;
    try {
      const payload = JSON.parse(event.data);
      if (payload?.type !== 'products.invalidated') return;
      if (String(payload.companyId) !== String(config.companyId)
        || String(payload.branchId) !== String(config.branchId)) return;
      notifyListeners(payload);
      if (payload.action === MAIN_BRANCH_CHANGED_ACTION) {
        reconnectWithFreshScope();
      }
    } catch (error) {
      console.error('Error handling client catalog WebSocket message:', error);
    }
  };
  nextSocket.onclose = () => {
    if (socket === nextSocket) socket = null;
    if (generation !== connectionGeneration || !running) return;
    scheduleReconnect();
  };
  scheduleScopeRefresh(config.expiresInSeconds);
};

export const startClientCatalogEvents = () => {
  if (running && (socket || isOpening)) return Promise.resolve();
  running = true;
  reconnectAttempts = 0;
  connectionGeneration += 1;
  isOpening = false;
  clearReconnect();
  clearScopeRefresh();
  closeSocket();
  return openSocket();
};

export const stopClientCatalogEvents = () => {
  running = false;
  reconnectAttempts = 0;
  connectionGeneration += 1;
  isOpening = false;
  clearReconnect();
  clearScopeRefresh();
  closeSocket();
};

export const subscribeClientCatalogEvents = (listener) => {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
};
